import { apiGet, planningPath } from './PlanningApiClient';
import { SeatingPlan } from '../types/Planner';

// ── Seating plans (in-memory cache) ──
let seatingPlansCache: SeatingPlan[] = [];

export const loadSeatingPlans = (): SeatingPlan[] => seatingPlansCache;

export const saveSeatingPlans = (plans: SeatingPlan[]): void => {
    seatingPlansCache = plans;
};

export const getSeatingPlan = (planId: string): SeatingPlan | undefined =>
    seatingPlansCache.find(p => p.id === planId);

export const saveSeatingPlan = (plan: SeatingPlan): void => {
    const idx = seatingPlansCache.findIndex(p => p.id === plan.id);
    if (idx >= 0) {
        seatingPlansCache = seatingPlansCache.map((p, i) => i === idx ? plan : p);
    } else {
        seatingPlansCache = [...seatingPlansCache, plan];
    }
};

// ── Seating tables (API-backed) ──

export interface SeatingTable {
  id: number;
  number: number;
  name: string;
  section: string;
  capacity: number;
}

let cache: SeatingTable[] | null = null;

export async function fetchSeatingTables(): Promise<SeatingTable[]> {
  if (cache) return cache;
  try {
    const data = await apiGet<SeatingTable[] | { tables: SeatingTable[] }>(
      planningPath('/seating/tables')
    );
    const tables = Array.isArray(data) ? data : (data as { tables: SeatingTable[] }).tables ?? [];
    cache = tables;
    return tables;
  } catch {
    return [];
  }
}

export function matchSection(zoneName: string, tables: SeatingTable[]): SeatingTable[] {
  const lower = zoneName.toLowerCase();
  // section is typed as a required string, but rows written before that
  // column was backfilled (or via a path that skips it) can still carry
  // null/'' at runtime — toLowerCase() on one of those crashed the whole
  // page the moment a floor plan with an unsectioned table rendered.
  const sections = [...new Set(tables.map(t => t.section).filter((s): s is string => !!s))];
  const matched = sections.find(s => lower.includes(s.toLowerCase()));
  if (!matched) return [];
  return tables.filter(t => t.section === matched);
}
